/**
 * Biblioteca da sala (system/roomLibrary; FC.Room.UIModule.RoomLibrary): pastas e
 * fichas compartilhadas, guardadas no servidor. As fichas são editadas pelos
 * plugins web instalados na mesa.
 *
 * - "+" cria pasta e cena (mestres) ou ficha ("Nova ficha…" abre a escolha do
 *   sistema entre os plugins da mesa).
 * - Duplo clique (ou Enter) abre a ficha numa aba ao lado do chat.
 * - Clique direito: abrir, renomear, mover para outra pasta, apagar e, para o mestre, tornar
 *   visível para todos ou invisível (BRAZ-16). Os itens nascem ocultos dos jogadores, e o mestre
 *   os vê com a marca de oculto.
 * - Mestres arrastam fichas, mapas e pastas para dentro de outra pasta (ou para o
 *   espaço vazio da lista, que é a raiz).
 * - Mestres instalam plugins pelo mesmo menu (atalho). Remover plugins e ativar
 *   as tabelas da sala fica em Configurações da mesa > Plugins, para o menu não crescer
 *   com a quantidade de plugins.
 */
import { useCallback, useMemo, useRef, useState, type DragEvent, type MouseEvent } from 'react';
import { useT } from '../../i18n/index.js';
import { visibleInHierarchy, type LibraryItem } from '../../shared.js';
import { api } from '../../api.js';
import { useApp } from '../../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../../ui/ContextMenu.js';
import { toast } from '../../ui/Toaster.js';
import { SearchBox } from '../../ui/SearchBox.js';
import { coarsePointer, isTouch, useLongPress } from '../../ui/touch.js';
import { isMobileNow } from '../../ui/mobile.js';
import { askChoice, askConfirm, askText } from '../../ui/prompt.js';
import { openPluginInstall } from '../../plugins/web/install.js';
import { isSheetType } from '../../plugins/pluginTypes.js';
import { openDoc, releaseDoc } from '../../plugins/ndbClient.js';
import { exportNodeToXml, importXmlOps } from '../../ndb.js';
import { canMoveInto, isMovable, moveTargets } from '../libraryTree.js';
import { NewSheetDialog } from '../NewSheetDialog.js';
import { roomPeople } from '../sheetOwners.js';

const DRAG_TYPE = 'application/x-rpgdesk-library-item';
/** tempo parado sobre uma pasta fechada até ela abrir durante o arraste */
const DRAG_EXPAND_MS = 700;

export function LibraryModule() {
  const t = useT();
  const library = useApp((s) => s.library);
  const plugins = useApp((s) => s.plugins);
  const room = useApp((s) => s.room);
  const me = useApp((s) => s.user);
  const myModes = useApp((s) => (s.user ? s.members[s.user.id]?.modes : undefined));
  const createItem = useApp((s) => s.createLibraryItem);
  const renameItem = useApp((s) => s.renameLibraryItem);
  const moveItem = useApp((s) => s.moveLibraryItem);
  const deleteItem = useApp((s) => s.deleteLibraryItem);
  const openSheet = useApp((s) => s.requestOpenSheet);
  const setMainCharacter = useApp((s) => s.setMainCharacter);
  const setLibraryOwner = useApp((s) => s.setLibraryOwner);
  const setLibraryVisible = useApp((s) => s.setLibraryVisible);
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const myMainCharacter = useApp((s) => (s.user ? s.members[s.user.id]?.mainCharacterId : null));
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; item: LibraryItem | null; moving?: boolean } | null>(null);
  // no dedo, segurar abre o menu (o iPhone não tem o botão direito)
  const press = useLongPress();
  // arrastar para dentro de pastas: o item arrastado e o destino em destaque (null = raiz)
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<string | null | undefined>(undefined);
  const expandTimer = useRef<{ folderId: string; timer: number } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  // "Nova ficha…": a pasta onde a ficha vai nascer
  const [newSheetIn, setNewSheetIn] = useState<{ parentId: string | null } | null>(null);
  const xmlRef = useRef<HTMLInputElement>(null);
  const importTarget = useRef<LibraryItem | null>(null);

  /** Ficha em XML no formato do NDB (cópia de segurança, ou para levar a outra mesa). */
  async function exportSheet(item: LibraryItem) {
    const db = await openDoc(item.id);
    try {
      const xml = exportNodeToXml(db.visible(), db.visible().root.id);
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.name.replace(/[\\/:*?"<>|]+/g, '_')}.xml`;
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      releaseDoc(item.id);
    }
  }


  async function importSheet(item: LibraryItem, file: File) {
    const sure = await askConfirm({ title: t('library.importXml'), message: t('library.importXml.confirm', { name: item.name, file: file.name }), action: t('library.importXml.go'), danger: true });
    if (!sure) return;
    const text = await file.text();
    const db = await openDoc(item.id);
    try {
      for (const batch of importXmlOps(text, db.visible().root.id, () => db.newId())) db.write(batch);
      toast(t('library.importXml.done', { name: item.name }), 'success');
    } catch (error) {
      toast(t('library.importXml.failed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    } finally {
      releaseDoc(item.id);
    }
  }

  const isGm = !!me && !!room && (room.ownerId === me.id || !!myModes?.includes('gm'));
  const isPlayer = !!myModes?.includes('player');
  const hasSheetTypes = plugins.some((p) => p.dataTypes.some(isSheetType));

  const children = useMemo(() => {
    const map = new Map<string | null, LibraryItem[]>();
    // as tabelas da sala (dock) ficam fora da árvore: abrem embaixo da biblioteca
    for (const item of library) {
      if (item.kind === 'dock' || item.kind === 'macros') continue;
      const list = map.get(item.parentId) ?? [];
      list.push(item);
      map.set(item.parentId, list);
    }
    for (const list of map.values()) list.sort((a, b) => (a.kind === b.kind ? a.position - b.position || a.name.localeCompare(b.name) : a.kind === 'folder' ? -1 : 1));
    return map;
  }, [library]);

  const q = query.trim().toLowerCase();
  const matches = (item: LibraryItem): boolean => !q || item.name.toLowerCase().includes(q) || (children.get(item.id) ?? []).some(matches);

  const canEdit = (item: LibraryItem) => isGm || (item.kind === 'sheet' && item.ownerId === me?.id);

  function createEntries(parentId: string | null): MenuEntry[] {
    const entries: MenuEntry[] = [];
    if (isGm)
      entries.push({
        label: t('library.newFolder'),
        onSelect: async () => {
          const name = await askText({ title: t('library.newFolder'), initial: t('library.folder'), action: t('library.create') });
          if (name) createItem({ kind: 'folder', name, parentId });
        },
      });
    if (isGm)
      entries.push({
        label: t('library.newScene'),
        onSelect: async () => {
          const name = await askText({ title: t('library.newScene'), initial: t('library.scene'), action: t('library.create') });
          if (name) createItem({ kind: 'scene', name, parentId });
        },
      });
    if (isGm || isPlayer) {
      // uma entrada só, por mais plugins que a mesa tenha: o sistema é escolhido no popup
      if (hasSheetTypes) entries.push({ label: t('library.newSheet'), onSelect: () => setNewSheetIn({ parentId }) });
      else entries.push({ label: t('library.noPlugins'), onSelect: () => undefined, disabled: true });
    }
    if (isGm) {
      entries.push({ kind: 'separator' });
      entries.push({ label: t('library.installPlugin'), onSelect: () => openPluginInstall() });
    }
    return entries;
  }

  function itemEntries(item: LibraryItem): MenuEntry[] {
    const entries: MenuEntry[] = [{ kind: 'title', label: item.name }];
    if (item.kind !== 'folder') entries.push({ label: t('library.open'), onSelect: () => openSheet(item.id) });
    // o mesmo, já numa janela própria (docs/spec/popout-windows.md); no celular não há janelas
    if (item.kind !== 'folder' && !isMobileNow()) entries.push({ label: t('library.openWindow'), onSelect: () => openSheet(item.id, { window: true }) });
    if (item.kind === 'sheet') entries.push({ label: t('library.exportXml'), onSelect: () => void exportSheet(item) });
    if (item.kind === 'sheet' && canEdit(item))
      entries.push({
        label: t('library.importXml'),
        onSelect: () => {
          importTarget.current = item;
          xmlRef.current?.click();
        },
      });
    if (item.kind === 'sheet' && (item.ownerId === me?.id || isGm)) {
      const isMain = myMainCharacter === item.id;
      entries.push({ label: isMain ? t('library.unsetMain') : t('library.setMain'), onSelect: () => setMainCharacter(isMain ? null : item.id) });
    }
    if (item.kind === 'sheet' && isGm) entries.push({ label: t('library.menu.setOwner'), onSelect: () => void changeOwner(item) });
    if (item.kind === 'folder') entries.push(...createEntries(item.id).filter((e) => e.kind !== 'separator' && !('label' in e && e.label === t('library.installPlugin'))));
    if (canEdit(item)) {
      entries.push({ kind: 'separator' });
      // no lugar do original: logo antes de "Renomear"
      if (isGm) entries.push({ label: t(item.visible ? 'library.menu.invisible' : 'library.menu.visible'), onSelect: () => setLibraryVisible(item.id, !item.visible) });
      entries.push({
        label: t('library.rename'),
        onSelect: async () => {
          const name = await askText({ title: t('library.rename'), initial: item.name, action: t('library.rename') });
          if (name && name !== item.name) renameItem(item.id, name);
        },
      });
      if (isGm && isMovable(item) && (item.parentId || moveTargets(library, item).length))
        entries.push({ label: t('library.moveTo'), onSelect: () => menu && setMenu({ anchor: menu.anchor, item, moving: true }) });
      entries.push({
        label: t('library.delete'),
        danger: true,
        onSelect: async () => {
          const sure = await askConfirm({
            title: t('library.delete'),
            message: t(item.kind === 'folder' ? 'library.delete.folderConfirm' : 'library.delete.confirm', { name: item.name }),
            action: t('library.delete'),
            danger: true,
          });
          if (sure) deleteItem(item.id);
        },
      });
    }
    return entries;
  }

  /** O mestre passa a ficha para alguém da mesa, ou para ninguém (só os mestres editam). */
  async function changeOwner(item: LibraryItem) {
    const NONE = '-';
    const choice = await askChoice({
      title: t('library.menu.setOwner'),
      label: t('library.setOwner.prompt', { sheet: item.name }),
      options: [
        ...roomPeople(users, members).map((p) => ({
          value: p.id,
          label: p.id === me?.id ? t('library.owner.me', { name: p.name }) : p.name,
          hint: t(`users.group.${p.role}.one`),
          current: item.ownerId === p.id,
        })),
        { value: NONE, label: t('library.owner.none'), current: item.ownerId === null },
      ],
    });
    if (choice === null) return;
    const ownerId = choice === NONE ? null : choice;
    if (ownerId !== item.ownerId) setLibraryOwner(item.id, ownerId);
  }

  /** "Mover para…": a raiz e as pastas onde o item pode entrar. */
  function moveEntries(item: LibraryItem): MenuEntry[] {
    const entries: MenuEntry[] = [{ kind: 'title', label: t('library.moveTo.title', { name: item.name }) }];
    if (item.parentId) entries.push({ label: t('library.moveTo.root'), onSelect: () => moveItem(item.id, null) });
    for (const target of moveTargets(library, item)) entries.push({ label: target.path, onSelect: () => moveInto(item.id, target.id) });
    return entries;
  }

  function expand(folderId: string) {
    setCollapsed((c) => {
      if (!c.has(folderId)) return c;
      const next = new Set(c);
      next.delete(folderId);
      return next;
    });
  }

  /** Move e abre a pasta de destino, para o item aparecer onde foi parar. */
  function moveInto(itemId: string, parentId: string | null) {
    moveItem(itemId, parentId);
    if (parentId) expand(parentId);
  }

  /* ---------------------------------------------------------------- arrastar */

  const dragged = dragId ? library.find((i) => i.id === dragId) : undefined;

  function clearExpandTimer() {
    if (expandTimer.current) window.clearTimeout(expandTimer.current.timer);
    expandTimer.current = null;
  }

  function endDrag() {
    clearExpandTimer();
    setDragId(null);
    setDropAt(undefined);
  }

  /** Sobre `parentId` (null = raiz): aceita o arraste se for um destino válido. */
  function dragOver(e: DragEvent, parentId: string | null) {
    if (!dragged || !e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.stopPropagation();
    if (!canMoveInto(library, dragged, parentId)) {
      e.dataTransfer.dropEffect = 'none';
      if (dropAt !== undefined) setDropAt(undefined);
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropAt !== parentId) setDropAt(parentId);
    // pasta fechada abre se o arraste ficar parado sobre ela
    if (parentId && collapsed.has(parentId) && expandTimer.current?.folderId !== parentId) {
      clearExpandTimer();
      expandTimer.current = { folderId: parentId, timer: window.setTimeout(() => expand(parentId), DRAG_EXPAND_MS) };
    }
  }

  function drop(e: DragEvent, parentId: string | null) {
    if (!dragged || !e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    if (canMoveInto(library, dragged, parentId)) moveInto(dragged.id, parentId);
    endDrag();
  }

  const openMenu = (item: LibraryItem | null) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ anchor: { x: e.clientX, y: e.clientY }, item });
  };

  const renderItems = (parentId: string | null, depth: number) =>
    (children.get(parentId) ?? []).filter(matches).map((item) => {
      const open = item.kind === 'folder' && (q !== '' || !collapsed.has(item.id));
      // só o mestre vê o que está oculto: o próprio item, ou uma pasta acima dele
      const hidden = isGm && (!item.visible ? 'self' : !visibleInHierarchy(library, item) ? 'folder' : null);
      return (
        <li key={item.id} role="treeitem" aria-expanded={item.kind === 'folder' ? open : undefined} aria-selected={false}>
          <div
            className={`rd-library__item rd-library__item--${item.kind}${hidden ? ' rd-library__item--hidden' : ''}${dropAt === item.id ? ' rd-library__item--drop' : ''}${dragId === item.id ? ' rd-library__item--dragging' : ''}`}
            style={{ paddingLeft: 10 + depth * 16 }}
            tabIndex={0}
            {...press.bind((anchor) => setMenu({ anchor, item }))}
            // no toque, segurar é o menu (no Android, segurar um arrastável começaria um arrasto):
            // mover fica no "Mover para…" do menu
            draggable={isGm && isMovable(item) && !coarsePointer()}
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_TYPE, item.id);
              e.dataTransfer.effectAllowed = 'move';
              setDragId(item.id);
            }}
            onDragEnd={endDrag}
            // soltar sobre uma ficha ou um mapa leva para a pasta onde ele está
            onDragOver={(e) => dragOver(e, item.kind === 'folder' ? item.id : item.parentId)}
            onDrop={(e) => drop(e, item.kind === 'folder' ? item.id : item.parentId)}
            onDoubleClick={() => (item.kind !== 'folder' ? openSheet(item.id) : undefined)}
            onClick={(e) => {
              if (item.kind === 'folder')
                setCollapsed((c) => {
                  const next = new Set(c);
                  if (next.has(item.id)) next.delete(item.id);
                  else next.add(item.id);
                  return next;
                });
              // no dedo, tocar abre: o duplo toque vira zoom e não chega como duplo clique
              else if (isTouch(e)) openSheet(item.id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && item.kind !== 'folder') openSheet(item.id);
              if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setMenu({ anchor: { x: r.left + 20, y: r.bottom }, item });
              }
            }}
          >
            {item.kind === 'folder' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d={open ? 'M3 6h7l2 2h9v2H6l-3 8z M6 11h17l-3 8H3z' : 'M3 5h7l2 2h9v12H3z'} />
              </svg>
            ) : item.kind === 'scene' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z M9 4v14M15 6v14" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M6 3h9l4 4v14H6z M9 11h7M9 15h7M9 7h4" />
              </svg>
            )}
            <span className="rd-library__name">{item.name}</span>
            {hidden ? (
              <span className={`rd-library__hiddenMark rd-library__hiddenMark--${hidden}`} role="img" aria-label={t(hidden === 'self' ? 'library.hidden' : 'library.hiddenByFolder')} title={t(hidden === 'self' ? 'library.hidden' : 'library.hiddenByFolder')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M3 4l17 17M10.6 6.1A10 10 0 0112 6c6 0 10 6 10 6a17 17 0 01-3 3.6M6.6 7.6A17 17 0 002 12s4 6 10 6a9 9 0 004-1" />
                </svg>
              </span>
            ) : null}
            {item.kind === 'sheet' && myMainCharacter === item.id ? <span className="rd-library__mine">{t('library.main')}</span> : null}
            {item.kind === 'sheet' && item.ownerId === me?.id && myMainCharacter !== item.id ? <span className="rd-library__mine">{t('library.mine')}</span> : null}
          </div>
          {open ? <ul role="group">{renderItems(item.id, depth + 1)}</ul> : null}
        </li>
      );
    });

  const rootItems = renderItems(null, 0);
  return (
    <div
      className="rd-library"
      onContextMenu={openMenu(null)}
      onDragOver={(e) => dragOver(e, null)}
      onDrop={(e) => drop(e, null)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          clearExpandTimer();
          setDropAt(undefined);
        }
      }}
    >
      <div className="rd-library__top">
        <SearchBox className="rd-library__search" value={query} onChange={setQuery} placeholder={t('library.search')} />
        {isGm || isPlayer ? (
          <button type="button" className="rd-library__add" title={t('library.add')} aria-label={t('library.add')} onClick={(e) => setMenu({ anchor: { x: e.clientX, y: e.clientY }, item: null })}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M4 4h12v2H6v12H4zm4 4h12v12H8zm5 2v3h-3v2h3v3h2v-3h3v-2h-3v-3z" />
            </svg>
          </button>
        ) : null}
      </div>
      <input
        ref={xmlRef}
        type="file"
        accept=".xml,application/xml,text/xml"
        hidden
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          const target = importTarget.current;
          importTarget.current = null;
          if (file && target) void importSheet(target, file);
        }}
      />
      <ul className={`rd-library__tree${dropAt === null ? ' rd-library__tree--drop' : ''}`} role="tree" aria-label={t('module.library')}>
        {rootItems}
        {library.length === 0 ? <li className="rd-library__empty">{t(hasSheetTypes ? 'library.empty' : isGm ? 'library.empty.gm' : 'library.empty')}</li> : null}
      </ul>
      {menu ? (
        <ContextMenu
          key={menu.moving ? 'move' : 'menu'}
          anchor={menu.anchor}
          entries={menu.item ? (menu.moving ? moveEntries(menu.item) : itemEntries(menu.item)) : createEntries(null)}
          label={menu.moving ? t('library.moveTo') : t('module.library')}
          onClose={closeMenu}
        />
      ) : null}
      {newSheetIn ? <NewSheetDialog parentId={newSheetIn.parentId} onClose={() => setNewSheetIn(null)} /> : null}
    </div>
  );
}
